/**
 * Competitors — who else makes what we make, and whose truck they are on.
 *
 * Two lenses on one page, **separated and labelled, never merged**:
 *
 * 1. **Manufacturers**, found by searching the open web per product. Ranked by
 *    how many *different* product searches found them, then by how many
 *    distinct sources. Times Seen is displayed and orders nothing — six hits
 *    from one search is one finding seen six times.
 * 2. **Brands on captured menus**, which is what a guest reads. Ranked on
 *    citrus lines, because by accounts the list is just the soda every
 *    restaurant in the country pours.
 *
 * They barely overlap and that is the point: Perricone Farms is on nobody's
 * menu, and Minute Maid's 39 accounts appear in no manufacturer search.
 *
 * **Sun Orchard makes the house brands for Sysco, US Foods and Markon.** A
 * broadline distributor and its house label are us, not a competitor — they
 * appear only as a route to market, and a competitor already on one of those
 * routes is the sharpest row on the page.
 *
 * Nothing is computed here; every figure arrives in the payload.
 */

import { useState } from 'react'

import type { CustomReportProps } from '..'
import type { Cell } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { dataset, text } from '../shared/rows'

import '../report.css'

const NONE = '—'
const TRUE = 'true'

function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function whole(value: Cell | undefined): string {
  const parsed = num(value)
  return parsed === null ? NONE : Math.round(parsed).toLocaleString('en-US')
}

function host(url: string | null): string | null {
  if (url === null) return null
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url.slice(0, 48)
  }
}

function Kpi({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <article className="cm-kpi">
      <p className="cm-kpi-label">{label}</p>
      <p className="cm-kpi-value cell-number">{value}</p>
      {detail !== undefined && <p className="cm-kpi-detail">{detail}</p>}
    </article>
  )
}

export function Competitors({ result }: CustomReportProps) {
  const headline = dataset(result, 'headline')[0]
  const makers = dataset(result, 'makers')
  const evidence = dataset(result, 'evidence')
  const routes = dataset(result, 'routes')
  const onMenus = dataset(result, 'on_menus')
  const holdouts = dataset(result, 'holdouts')
  const asOf = dataset(result, 'as_of')

  // Opening a manufacturer is a reading aid over the loaded payload, not a
  // filter on the data — the param bar owns what gets loaded.
  const [open, setOpen] = useState<number | null>(null)
  const openEvidence =
    open === null ? [] : evidence.filter((row) => num(row.competitor_id) === open)

  return (
    <div className="report-stack">
      <section className="cm-kpis">
        <Kpi
          label="Manufacturers"
          value={whole(headline?.makers)}
          detail="found by product search"
        />
        <Kpi label="Confirmed" value={whole(headline?.confirmed)} detail="a human said yes" />
        <Kpi
          label="On a broadline route"
          value={whole(headline?.on_route)}
          detail="in the same truck we are"
        />
        <Kpi label="Claim NFC" value={whole(headline?.nfc)} detail="not from concentrate" />
        <Kpi
          label="Pieces of evidence"
          value={whole(headline?.observations)}
          detail="behind the list below"
        />
        <Kpi
          label="Brands on menus"
          value={whole(headline?.menu_brands)}
          detail="a separate question"
        />
      </section>

      <Card
        title="Manufacturers"
        subtitle="Ranked by how many different product searches found them, then by distinct sources. Times Seen orders nothing — repetition is not corroboration. Click a row for the evidence."
      >
        {makers.length === 0 ? (
          <Empty label="No manufacturer matches these filters." />
        ) : (
          <div className="cm-scroll">
            <table className="cm-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>What they make</th>
                  <th className="cell-number">Terms</th>
                  <th className="cell-number">Sources</th>
                  <th className="cell-number">Seen</th>
                  <th>Carried by</th>
                  <th>Found by</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {makers.map((row, index) => {
                  const id = num(row.competitor_id)
                  const isOpen = id !== null && id === open
                  const onRoute = text(row.on_a_route) === TRUE
                  const website = text(row.website)
                  return (
                    <tr
                      key={text(row.name) ?? String(index)}
                      className={`cm-row${isOpen ? ' is-open' : ''}`}
                      onClick={() => {
                        setOpen(isOpen ? null : id)
                      }}
                    >
                      <td>
                        <span className={`cm-co${onRoute ? ' is-route' : ''}`}>
                          {text(row.name) ?? NONE}
                        </span>
                        {text(row.claims_nfc) === TRUE && (
                          <span className="cm-tag is-nfc">NFC</span>
                        )}
                        {website !== null && (
                          <a
                            className="cm-where cm-link"
                            href={website}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) => {
                              event.stopPropagation()
                            }}
                          >
                            {host(website)}
                          </a>
                        )}
                      </td>
                      <td className="cm-wrap cm-dim">{text(row.what_they_make) ?? NONE}</td>
                      <td className="cell-number cm-rank">{whole(row.found_by_terms)}</td>
                      <td className="cell-number">{whole(row.evidence_urls)}</td>
                      <td className="cell-number cm-dim">{whole(row.times_seen)}</td>
                      <td>
                        {onRoute ? (
                          <span className="cm-tag is-route">{text(row.distributors)}</span>
                        ) : (
                          <span className="cm-dim">{NONE}</span>
                        )}
                      </td>
                      <td className="cm-wrap cm-dim">{text(row.keywords) ?? NONE}</td>
                      <td>
                        <span className={`cm-tag is-${text(row.status) ?? 'candidate'}`}>
                          {text(row.status) ?? NONE}
                        </span>
                        {text(row.decided_by) !== null && (
                          <span className="cm-where">{text(row.decided_by)}</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {open !== null && (
        <Card
          title="Evidence"
          subtitle="Every page that found this company, the search term behind it, and why it counted."
          actions={
            <button
              type="button"
              className="cm-chip"
              onClick={() => {
                setOpen(null)
              }}
            >
              Close
            </button>
          }
        >
          {openEvidence.length === 0 ? (
            <Empty label="No stored observations for this company." />
          ) : (
            <table className="cm-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Why it counts</th>
                  <th>Distributors named</th>
                  <th>Observed</th>
                </tr>
              </thead>
              <tbody>
                {openEvidence.map((row, index) => {
                  const url = text(row.evidence_url)
                  return (
                    <tr key={`${url ?? 'row'}-${String(index)}`}>
                      <td>
                        {url === null ? (
                          NONE
                        ) : (
                          <a className="cm-link" href={url} target="_blank" rel="noreferrer">
                            {host(url)}
                          </a>
                        )}
                      </td>
                      <td className="cm-wrap cm-dim">{text(row.why) ?? NONE}</td>
                      <td>{text(row.distributors) ?? NONE}</td>
                      <td className="cell-number cm-dim">{text(row.observed_on) ?? NONE}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </Card>
      )}

      <div className="cm-columns">
        <Card
          title="Broadliners carrying a competitor"
          subtitle="Not rivals on their own routes — the same trucks we are in, in front of the same buyer, beside the house brand we manufacture."
        >
          {routes.length === 0 ? (
            <Empty label="No competitor is on a known broadline route." />
          ) : (
            <table className="cm-table">
              <thead>
                <tr>
                  <th>Distributor</th>
                  <th className="cell-number">Carries</th>
                  <th>Who</th>
                </tr>
              </thead>
              <tbody>
                {routes.map((row, index) => (
                  <tr key={text(row.distributor) ?? String(index)}>
                    <td className="cm-co is-route">{text(row.distributor) ?? NONE}</td>
                    <td className="cell-number">{whole(row.competitors)}</td>
                    <td className="cm-wrap cm-dim">{text(row.carried_names) ?? NONE}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card
          title="Brands a guest can read"
          subtitle="A different question, kept separate. Ranked on citrus lines — by accounts this list is the soda every restaurant pours."
        >
          {onMenus.length === 0 ? (
            <Empty label="No brand matched on the captured menus." />
          ) : (
            <div className="cm-scroll cm-short">
              <table className="cm-table">
                <thead>
                  <tr>
                    <th>Brand</th>
                    <th className="cell-number">Citrus</th>
                    <th className="cell-number">Accounts</th>
                    <th className="cell-number">Lines</th>
                    <th>Seen at</th>
                  </tr>
                </thead>
                <tbody>
                  {onMenus.map((row, index) => (
                    <tr key={text(row.name) ?? String(index)}>
                      <td>
                        <span className="cm-co">{text(row.name) ?? NONE}</span>
                        {text(row.claims_nfc) === TRUE && (
                          <span className="cm-tag is-nfc">NFC</span>
                        )}
                        <span className="cm-where">{text(row.kinds) ?? ''}</span>
                      </td>
                      <td className="cell-number cm-rank">{whole(row.citrus_lines)}</td>
                      <td className="cell-number">{whole(row.accounts)}</td>
                      <td className="cell-number cm-dim">{whole(row.menu_lines)}</td>
                      <td className="cm-wrap cm-dim">{text(row.evidence_url) ?? NONE}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {holdouts.length > 0 && (
        <Card
          title="Held back by the ranked view"
          subtitle="Found by the search but filtered out upstream as not-foodservice, retail-only or rejected. Shown because the count above is what survived that filter, not what the search found."
        >
          <table className="cm-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>What they make</th>
                <th>Why it is held back</th>
                <th>Review state</th>
              </tr>
            </thead>
            <tbody>
              {holdouts.map((row, index) => {
                const website = text(row.website)
                return (
                  <tr key={text(row.name) ?? String(index)}>
                    <td>
                      <span className="cm-co">{text(row.name) ?? NONE}</span>
                      {website !== null && (
                        <a
                          className="cm-where cm-link"
                          href={website}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {host(website)}
                        </a>
                      )}
                    </td>
                    <td className="cm-wrap cm-dim">{text(row.what_they_make) ?? NONE}</td>
                    <td>
                      <span className="cm-tag is-candidate">{text(row.why) ?? NONE}</span>
                    </td>
                    <td className="cm-dim">{text(row.status) ?? NONE}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Card title="What this is, and what it is not">
        <table className="cm-table">
          <tbody>
            {asOf.map((row, index) => (
              <tr key={text(row.source) ?? String(index)}>
                <td className="cm-field">{text(row.source) ?? NONE}</td>
                <td className="cm-asof-val">{text(row.as_of) ?? NONE}</td>
                <td className="cm-wrap">
                  <p className="cm-note">{text(row.note) ?? ''}</p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
